// src/components/BillingDashboard.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../contexts/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  ToggleButtonGroup,
  ToggleButton,
  AlertTitle
} from '@mui/material';
import {
  CreditCard,
  Package,
  Calendar,
  CheckCircle,
  XCircle,
  Download,
  FileText,
  Clock,
  Edit,
  Plus,
  Trash2,
  DollarSign,
  BarChart,
  ChevronRight,
  ChevronLeft,
  Shield,
  AlertCircle,
  Check,
  Zap,
  Calendar as CalendarIcon,
  Sparkles,
  Printer,
  Save,
  FilePlus,
  Receipt,
  Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// Base subscription plans with monthly pricing
const baseSubscriptionPlans = [
  {
    id: 'basic',
    name: 'Basic',
    monthlyPrice: 1.99,
    features: [
      'Create professional profile',
      'Generate QR codes',
      'Create custom email signatures',
      'Export profile as HTML/PHP',
      'No trial limitations'
    ],
    recommended: false
  },
  {
    id: 'pro',
    name: 'Professional',
    monthlyPrice: 3.99,
    features: [
      'Everything in Basic',
      'Unlimited Profile Exports',
      'Advanced Analytics',
      'Priority Support',
      'Custom Domain for Profile'
    ],
    recommended: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 49.99,
    features: [
      'Everything in Professional',
      'Team Management',
      'API Access',
      'Dedicated Account Manager',
      'Custom Branding',
      'SSO Integration'
    ],
    recommended: false
  }
];

const BillingDashboard = () => {
  const { currentUser, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    plan: 'pro',
    status: 'active',
    currentPeriodEnd: '2024-03-15',
    cancelAtPeriodEnd: false,
    billingCycle: 'monthly',
    paymentMethod: 'card'
  });
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [payments, setPayments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Dialog states
  const [addCardDialogOpen, setAddCardDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvc: ''
  });
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const invoiceRef = useRef(null);

  // Alert states
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });

  // Trial states - check if user is on trial
  const [trialInfo, setTrialInfo] = useState({
    isTrial: false,
    isExpired: false,
    daysRemaining: 0
  });
  
  // Generate a reference/invoice number
  const generateReferenceNumber = () => {
    const prefix = "INV";
    const userId = currentUser?.id || Math.floor(Math.random() * 10000);
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${userId}-${timestamp}`;
  };
  
  // Reference number for the current user
  const [bankReferenceNumber] = useState(generateReferenceNumber());
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Generate current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };
  
  // Generate due date (14 days from now) in YYYY-MM-DD format
  const getDueDate = () => {
    const now = new Date();
    const dueDate = new Date(now.setDate(now.getDate() + 14));
    return dueDate.toISOString().split('T')[0];
  };
  
  // Calculate next billing date based on current date and billing cycle
  const getNextBillingDate = (billingCycleType) => {
    const now = new Date();
    if (billingCycleType === 'yearly') {
      return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString().split('T')[0];
    } else {
      return new Date(now.setMonth(now.getMonth() + 1)).toISOString().split('T')[0];
    }
  };

  // Get all subscription plans with calculated yearly pricing
  const getSubscriptionPlans = () => {
    return baseSubscriptionPlans.map(plan => {
      const yearlyPrice = (plan.monthlyPrice * 12 * 0.8).toFixed(2); // 20% discount for yearly
      return {
        ...plan,
        price: billingCycle === 'monthly' ? plan.monthlyPrice : yearlyPrice,
        frequency: billingCycle,
        yearlyPrice: yearlyPrice,
        yearlySavings: ((plan.monthlyPrice * 12) - yearlyPrice).toFixed(2)
      };
    });
  };

  const subscriptionPlans = getSubscriptionPlans();

  // Get trial status from localStorage
  const getTrialStatus = () => {
    try {
      // First check for cached trial info
      const cachedTrialInfo = localStorage.getItem('trialInfo');
      if (cachedTrialInfo) {
        const parsedInfo = JSON.parse(cachedTrialInfo);
        if (parsedInfo && parsedInfo.isTrial) {
          return {
            isTrial: true,
            isExpired: new Date() > new Date(parsedInfo.trialEndDate),
            daysRemaining: parsedInfo.daysRemaining || 0
          };
        }
      }

      // Then check for _pmt encoded data
      const encodedTrialData = localStorage.getItem('_pmt');
      if (encodedTrialData) {
        try {
          const decoded = JSON.parse(atob(encodedTrialData));
          if (decoded && decoded.isTrial) {
            const endDate = new Date(decoded.endDate);
            return {
              isTrial: true,
              isExpired: new Date() > endDate,
              daysRemaining: Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)))
            };
          }
        } catch (e) {
          console.error('Error decoding trial data:', e);
        }
      }

      // Get trial info from user object
      if (currentUser?.is_trial) {
        const endDate = new Date(currentUser.trial_end_date);
        return {
          isTrial: true,
          isExpired: new Date() > endDate,
          daysRemaining: Math.max(0, Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)))
        };
      }
    } catch (e) {
      console.error('Error checking trial status:', e);
    }

    // Default values if no trial info is found
    return {
      isTrial: false,
      isExpired: false,
      daysRemaining: 0
    };
  };

  // Fetch subscription and trial data on mount
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        
        // Get trial status
        const trialStatus = getTrialStatus();
        setTrialInfo(trialStatus);

        // In a real implementation, fetch subscription data from API
        // For example:
        // const response = await axios.get(`${API_URL}/api/billing/subscription`);
        // setSubscriptionStatus(response.data);
        // setPayments(await fetchPaymentHistory());
        // setPaymentMethods(await fetchPaymentMethods());
        
        // If user is on trial, set subscription status accordingly
        if (trialStatus.isTrial) {
          setSubscriptionStatus({
            plan: 'trial',
            status: trialStatus.isExpired ? 'expired' : 'active',
            currentPeriodEnd: currentUser?.trial_end_date || new Date(Date.now() + trialStatus.daysRemaining * 24 * 60 * 60 * 1000).toISOString(),
            cancelAtPeriodEnd: false,
            billingCycle: 'monthly'
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setAlert({
          show: true,
          message: 'Failed to load subscription data. Please try again.',
          severity: 'error'
        });
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [currentUser]);

  // Handler for billing cycle toggle
  const handleBillingCycleChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setBillingCycle(newBillingCycle);
    }
  };

  // Get current subscription plan details or trial plan
  const getCurrentPlan = () => {
    if (subscriptionStatus.plan === 'trial') {
      return {
        id: 'trial',
        name: 'Trial',
        price: 0,
        frequency: 'once',
        features: [
          'Create professional profile',
          'Generate QR codes (preview only)',
          'Create email signatures (preview only)',
          'Export profile (preview only)',
          'Valid for 7 days'
        ],
        recommended: false
      };
    }
    
    // Find the base plan
    const basePlan = subscriptionPlans.find(plan => plan.id === subscriptionStatus.plan);
    
    if (!basePlan) {
      return subscriptionPlans[1]; // Default to Pro plan
    }
    
    // Return plan with correct pricing based on subscription's billing cycle
    return {
      ...basePlan,
      price: subscriptionStatus.billingCycle === 'yearly' ? basePlan.yearlyPrice : basePlan.monthlyPrice,
      frequency: subscriptionStatus.billingCycle
    };
  };

  const currentPlan = getCurrentPlan();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Dialog handlers
  const handleAddCardOpen = () => {
    setAddCardDialogOpen(true);
  };

  const handleAddCardClose = () => {
    setAddCardDialogOpen(false);
    setCardDetails({
      number: '',
      name: '',
      expiry: '',
      cvc: ''
    });
  };

  const handleAddCard = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to add payment method
      // For example:
      // await axios.post(`${API_URL}/api/billing/payment-methods`, cardDetails);
      // const updatedPaymentMethods = await fetchPaymentMethods();
      // setPaymentMethods(updatedPaymentMethods);
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAlert({
        show: true,
        message: 'Payment method added successfully',
        severity: 'success'
      });
      
      setAddCardDialogOpen(false);
      setLoading(false);
    } catch (error) {
      console.error('Error adding payment method:', error);
      setAlert({
        show: true,
        message: 'Failed to add payment method. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to delete payment method
      // For example:
      // await axios.delete(`${API_URL}/api/billing/payment-methods/${paymentMethodId}`);
      // const updatedPaymentMethods = await fetchPaymentMethods();
      // setPaymentMethods(updatedPaymentMethods);
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAlert({
        show: true,
        message: 'Payment method removed successfully',
        severity: 'success'
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error removing payment method:', error);
      setAlert({
        show: true,
        message: 'Failed to remove payment method. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to set default payment method
      // For example:
      // await axios.post(`${API_URL}/api/billing/payment-methods/${paymentMethodId}/default`);
      // const updatedPaymentMethods = await fetchPaymentMethods();
      // setPaymentMethods(updatedPaymentMethods);
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAlert({
        show: true,
        message: 'Default payment method updated',
        severity: 'success'
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error updating default payment method:', error);
      setAlert({
        show: true,
        message: 'Failed to update default payment method. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancelSubscriptionOpen = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelSubscriptionClose = () => {
    setCancelDialogOpen(false);
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to cancel subscription
      // For example:
      // await axios.post(`${API_URL}/api/billing/subscription/cancel`);
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update subscription status
      setSubscriptionStatus({
        ...subscriptionStatus,
        cancelAtPeriodEnd: true
      });
      
      setAlert({
        show: true,
        message: 'Your subscription will be canceled at the end of the billing period',
        severity: 'info'
      });
      
      setCancelDialogOpen(false);
      setLoading(false);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setAlert({
        show: true,
        message: 'Failed to cancel subscription. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to resume subscription
      // For example:
      // await axios.post(`${API_URL}/api/billing/subscription/resume`);
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update subscription status
      setSubscriptionStatus({
        ...subscriptionStatus,
        cancelAtPeriodEnd: false
      });
      
      setAlert({
        show: true,
        message: 'Your subscription has been resumed',
        severity: 'success'
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error resuming subscription:', error);
      setAlert({
        show: true,
        message: 'Failed to resume subscription. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleChangePlanOpen = () => {
    setSelectedPlan(currentPlan.id === 'trial' ? 'pro' : currentPlan.id);
    setSelectedBillingCycle(subscriptionStatus.billingCycle || 'monthly');
    setSelectedPaymentMethod(subscriptionStatus.paymentMethod || 'card');
    setChangePlanDialogOpen(true);
  };

  const handleChangePlanClose = () => {
    setChangePlanDialogOpen(false);
    setSelectedPlan(null);
    setSelectedBillingCycle('monthly');
    setSelectedPaymentMethod('card');
  };

  const handleChangePlan = async () => {
    try {
      setLoading(true);
      
      // In a real implementation, call API to change subscription plan
      // For example:
      // await axios.post(`${API_URL}/api/billing/subscription/change`, {
      //   plan: selectedPlan,
      //   billingCycle: selectedBillingCycle,
      //   paymentMethod: selectedPaymentMethod
      // });
      
      // Simulating API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update subscription plan
      setSubscriptionStatus({
        ...subscriptionStatus,
        plan: selectedPlan,
        billingCycle: selectedBillingCycle,
        paymentMethod: selectedPaymentMethod,
        cancelAtPeriodEnd: false
      });
      
      // If upgrading from trial, clear trial data
      if (trialInfo.isTrial) {
        localStorage.removeItem('_pmt');
        localStorage.removeItem('trialInfo');
        
        // Update trialInfo state
        setTrialInfo({
          isTrial: false,
          isExpired: false,
          daysRemaining: 0
        });
        
        // Refresh user data if available
        if (refreshUser) {
          await refreshUser();
        }
      }
      
      // Update current billing cycle if it changed
      if (selectedBillingCycle !== billingCycle) {
        setBillingCycle(selectedBillingCycle);
      }
      
      const planName = subscriptionPlans.find(plan => plan.id === selectedPlan)?.name;
      let successMessage = `Your subscription has been updated to the ${planName} plan (${selectedBillingCycle === 'yearly' ? 'Annual' : 'Monthly'})`;
      
      if (selectedPaymentMethod === 'invoice') {
        successMessage += `. Please complete your payment using the bank transfer details provided. Reference: ${bankReferenceNumber}`;
      }
      
      setAlert({
        show: true,
        message: successMessage,
        severity: 'success'
      });
      
      setChangePlanDialogOpen(false);
      setLoading(false);
    } catch (error) {
      console.error('Error changing subscription plan:', error);
      setAlert({
        show: true,
        message: 'Failed to change subscription plan. Please try again.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleUpgradeFromTrial = () => {
    setSelectedPlan('pro');
    setSelectedBillingCycle('monthly');
    setSelectedPaymentMethod('card');
    setChangePlanDialogOpen(true);
  };

  const handleDownloadInvoice = (invoiceId) => {
    // In a real implementation, call API to download invoice
    // For example:
    // window.open(`${API_URL}/api/billing/invoices/${invoiceId}/download`, '_blank');
    
    setAlert({
      show: true,
      message: 'Invoice download started',
      severity: 'success'
    });
  };

  const handleCardDetailsChange = (e) => {
    const { name, value } = e.target;
    setCardDetails({
      ...cardDetails,
      [name]: value
    });
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, show: false });
  };

  // Generate invoice data for current subscription
  const generateInvoiceData = () => {
    const planData = subscriptionPlans.find(plan => plan.id === subscriptionStatus.plan) || subscriptionPlans[0];
    const billingCycle = subscriptionStatus.billingCycle;
    const price = billingCycle === 'yearly' ? planData.yearlyPrice : planData.monthlyPrice;
    
    const invoiceNumber = generateReferenceNumber();
    const currentDate = getCurrentDate();
    const dueDate = getDueDate();
    const periodStart = currentDate;
    const periodEnd = getNextBillingDate(billingCycle);
    
    // Calculate tax (example: 10%)
    const taxRate = 0.10;
    const subtotal = parseFloat(price);
    const taxAmount = subtotal * taxRate;
    const total = subtotal + taxAmount;
    
    return {
      id: invoiceNumber,
      date: currentDate,
      dueDate: dueDate,
      companyDetails: {
        name: 'Your Company Name',
        address: '123 Business Street',
        city: 'San Francisco, CA 94103',
        email: 'billing@yourcompany.com',
        phone: '+1 (555) 123-4567',
        website: 'www.yourcompany.com'
      },
      customerDetails: {
        name: currentUser?.name || 'John Doe',
        email: currentUser?.email || 'john@example.com',
        address: currentUser?.address || '456 Customer Ave',
        city: currentUser?.city || 'San Francisco, CA 94105'
      },
      items: [
        {
          description: `${planData.name} Plan - ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription`,
          quantity: 1,
          unitPrice: subtotal,
          amount: subtotal
        }
      ],
      additionalNotes: billingCycle === 'yearly' ? 'Includes 20% discount for annual billing' : '',
      subtotal: subtotal,
      taxRate: taxRate * 100,
      taxAmount: taxAmount,
      total: total,
      paymentMethod: subscriptionStatus.paymentMethod,
      bankDetails: {
        name: 'Example Bank Ltd',
        accountNumber: '1234567890',
        routingNumber: '01-02-03',
        iban: 'GB29NWBK60161331926819'
      },
      status: 'Pending',
      periodStart: periodStart,
      periodEnd: periodEnd,
      reference: bankReferenceNumber
    };
  };
  
  // Handle opening the invoice generation dialog
  const handleGenerateInvoice = () => {
    setGeneratedInvoice(generateInvoiceData());
    setInvoiceDialogOpen(true);
  };
  
  // Handle closing the invoice dialog
  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
  };
  
  // Handle invoice download/print
  const handlePrintInvoice = () => {
    if (invoiceRef.current) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write('<html><head><title>Print Invoice</title>');
      
      // Add print-specific styles
      printWindow.document.write(`
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #f2f2f2; }
          .invoice-header { display: flex; justify-content: space-between; margin-bottom: 30px; }
          .invoice-footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 20px; }
          .text-right { text-align: right; }
          .text-bold { font-weight: bold; }
          @media print {
            body { padding: 0; }
            button { display: none; }
          }
        </style>
      `);
      
      printWindow.document.write('</head><body>');
      printWindow.document.write(invoiceRef.current.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      
      // Trigger print after the content is loaded
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
      };
    }
  };

  // Render trial banner if user is on trial
  const renderTrialBanner = () => {
    if (!trialInfo.isTrial) return null;
    
    return (
      <Alert 
        severity={trialInfo.isExpired ? "error" : "warning"}
        sx={{ mb: 3 }}
        action={
          <Button 
            color="inherit" 
            size="small" 
            variant="outlined"
            onClick={handleUpgradeFromTrial}
          >
            Upgrade Now
          </Button>
        }
      >
        {trialInfo.isExpired 
          ? 'Your trial has expired. Upgrade now to restore access to all features.' 
          : `Your trial will expire in ${trialInfo.daysRemaining} day${trialInfo.daysRemaining !== 1 ? 's' : ''}. Upgrade now to keep access to all features.`}
      </Alert>
    );
  };

  // Get the appropriate time period for the current billing cycle
  const getBillingPeriodLabel = (cycle) => {
    return cycle === 'yearly' ? 'year' : 'month';
  };

  // Render subscription details tab
  const renderSubscriptionTab = () => (
    <Box>
      {/* Show trial banner if applicable */}
      {renderTrialBanner()}
      
      <Grid container spacing={3}>
        {/* Current Plan */}
        <Grid item xs={12} md={7}>
          <Card elevation={2} sx={{ mb: 3, position: 'relative', overflow: 'hidden' }}>
            {/* Current Plan badge */}
            <Chip
              label="Current Plan"
              color="primary"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                fontWeight: 'bold'
              }}
            />
            
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5" fontWeight="bold">{currentPlan.name} Plan</Typography>
                  <Typography variant="h4" color="primary" sx={{ mt: 1, mb: 2 }}>
                    ${parseFloat(currentPlan.price).toFixed(2)}
                    <Typography component="span" variant="subtitle1" color="textSecondary">
                      /{currentPlan.id === 'trial' ? 'one-time' : getBillingPeriodLabel(subscriptionStatus.billingCycle)}
                    </Typography>
                  </Typography>
                  
                  {/* Show savings badge for yearly plans */}
                  {subscriptionStatus.billingCycle === 'yearly' && currentPlan.id !== 'trial' && (
                    <Chip
                      icon={<Sparkles size={16} />}
                      label="20% Savings"
                      color="success"
                      variant="outlined"
                      size="small"
                      sx={{ mb: 2 }}
                    />
                  )}
                  
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    {subscriptionStatus.plan === 'trial' ? (
                      <Chip 
                        icon={<Clock size={16} />}
                        label={trialInfo.isExpired ? "Expired" : `${trialInfo.daysRemaining} days remaining`} 
                        color={trialInfo.isExpired ? "error" : "warning"}
                        variant="outlined"
                      />
                    ) : (
                      <Chip 
                        icon={<CheckCircle size={16} />}
                        label={subscriptionStatus.cancelAtPeriodEnd ? "Cancels on " + formatDate(subscriptionStatus.currentPeriodEnd) : "Active"} 
                        color={subscriptionStatus.cancelAtPeriodEnd ? "warning" : "success"}
                        variant="outlined"
                      />
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      {subscriptionStatus.plan === 'trial' 
                        ? `Trial ends on: ${formatDate(subscriptionStatus.currentPeriodEnd)}`
                        : `Next billing date: ${formatDate(subscriptionStatus.currentPeriodEnd)}`}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Show billing cycle information for paid plans */}
                {subscriptionStatus.plan !== 'trial' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Chip
                      icon={<CalendarIcon size={16} />}
                      label={`${subscriptionStatus.billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Billing`}
                      color={subscriptionStatus.billingCycle === 'yearly' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      icon={subscriptionStatus.paymentMethod === 'card' ? <CreditCard size={16} /> : <FileText size={16} />}
                      label={subscriptionStatus.paymentMethod === 'card' ? 'Credit Card' : 'Bank Transfer'}
                      color={subscriptionStatus.paymentMethod === 'card' ? 'info' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
                Plan Features
              </Typography>
              
              <Grid container spacing={1}>
                {currentPlan.features?.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box display="flex" alignItems="center">
                      <CheckCircle size={16} color="#4CAF50" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                {currentPlan.id === 'trial' ? (
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleUpgradeFromTrial}
                    startIcon={<Zap />}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Upgrade from Trial'}
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outlined" 
                      color="primary"
                      onClick={handleChangePlanOpen}
                    >
                      Change Plan
                    </Button>
                    
                    {subscriptionStatus.cancelAtPeriodEnd ? (
                      <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleResumeSubscription}
                        disabled={loading}
                      >
                        Resume Subscription
                      </Button>
                    ) : (
                      <Button 
                        variant="outlined" 
                        color="error"
                        onClick={handleCancelSubscriptionOpen}
                        disabled={loading}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                    
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Receipt />}
                      onClick={handleGenerateInvoice}
                    >
                      Generate Invoice
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Payment Methods */}
        <Grid item xs={12} md={5}>
          {subscriptionStatus.paymentMethod === 'card' ? (
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">Payment Methods</Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<Plus size={16} />}
                    onClick={handleAddCardOpen}
                  >
                    Add New
                  </Button>
                </Box>
                
                {paymentMethods.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No payment methods found. Add a payment method to manage your subscription.
                  </Alert>
                ) : (
                  <List>
                    {paymentMethods.map((method) => (
                      <ListItem
                        key={method.id}
                        secondaryAction={
                          <Box>
                            {!method.isDefault && (
                              <>
                                <Button
                                  size="small"
                                  onClick={() => handleSetDefaultPaymentMethod(method.id)}
                                  sx={{ mr: 1 }}
                                >
                                  Set Default
                                </Button>
                                <IconButton 
                                  edge="end" 
                                  color="error"
                                  onClick={() => handleDeletePaymentMethod(method.id)}
                                >
                                  <Trash2 size={18} />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          <CreditCard />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              {method.type.charAt(0).toUpperCase() + method.type.slice(1)} ending in {method.last4}
                              {method.isDefault && (
                                <Chip 
                                  label="Default" 
                                  size="small" 
                                  color="primary" 
                                  sx={{ ml: 1 }} 
                                />
                              )}
                            </>
                          }
                          secondary={`Expires ${method.expMonth}/${method.expYear}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6">Bank Transfer Details</Typography>
                  <Chip
                    icon={<Clock size={16} />}
                    label="Pending Payment"
                    color="warning"
                    variant="outlined"
                  />
                </Box>
                
                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Please transfer payment to:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                    <li>
                      <Typography variant="body2">Bank: <strong>Example Bank Ltd</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Account Name: <strong>Your Company Name</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Account Number: <strong>1234567890</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Sort Code / Routing: <strong>01-02-03</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">IBAN: <strong>GB29NWBK60161331926819</strong></Typography>
                    </li>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Payment Reference
                  </Typography>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      bgcolor: 'primary.light', 
                      color: 'primary.contrastText',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {bankReferenceNumber}
                    </Typography>
                    <IconButton 
                      size="small" 
                      sx={{ color: 'inherit' }}
                      onClick={() => {
                        navigator.clipboard.writeText(bankReferenceNumber);
                        setAlert({
                          show: true,
                          message: 'Reference number copied to clipboard!',
                          severity: 'success'
                        });
                      }}
                    >
                      <Copy size={16} />
                    </IconButton>
                  </Paper>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Important:</strong> Include this reference number with your payment to ensure it is correctly applied to your account.
                  </Typography>
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Payment Processing</AlertTitle>
                  Please allow 1-3 business days for your payment to be processed and applied to your account.
                </Alert>
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={handleChangePlanOpen}
                >
                  Switch to Card Payment
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  // Render payment history tab
  const renderPaymentHistoryTab = () => (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>Payment History</Typography>
        
        {/* Show trial banner if applicable */}
        {trialInfo.isTrial && renderTrialBanner()}
        
        {payments.length === 0 ? (
          <Alert severity="info">
            No payment history found. Your payments will appear here once you make a payment.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Invoice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.date)}</TableCell>
                    <TableCell>{payment.description}</TableCell>
                    <TableCell align="right">${payment.amount.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                        color={payment.status === 'paid' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleDownloadInvoice(payment.id)}
                        color="primary"
                        size="small"
                        title="Download Invoice"
                      >
                        <Download size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  // Render plans comparison tab
  const renderPlansTab = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>Available Plans</Typography>
      
      {/* Show trial banner if applicable */}
      {trialInfo.isTrial && renderTrialBanner()}
      
      {/* Billing cycle toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {billingCycle === 'yearly' ? 'Save 20% with annual billing' : 'Switch to annual billing to save 20%'}
        </Typography>
        
        <ToggleButtonGroup
          value={billingCycle}
          exclusive
          onChange={handleBillingCycleChange}
          aria-label="billing cycle"
          size="small"
        >
          <ToggleButton value="monthly" aria-label="monthly billing">
            Monthly
          </ToggleButton>
          <ToggleButton value="yearly" aria-label="yearly billing">
            Yearly <Chip label="20% off" size="small" color="success" sx={{ ml: 1, height: 20 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Grid container spacing={3}>
        {subscriptionPlans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card 
              elevation={2} 
              sx={{ 
                height: '100%', 
                position: 'relative',
                border: plan.id === currentPlan.id ? '2px solid #2196f3' : 'none',
              }}
            >
              {plan.recommended && (
                <Chip
                  label="Recommended"
                  color="secondary"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    fontWeight: 'bold'
                  }}
                />
              )}
              
              {plan.id === currentPlan.id && (
                <Chip
                  label="Current Plan"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    fontWeight: 'bold'
                  }}
                />
              )}
              
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight="bold">{plan.name}</Typography>
                <Typography variant="h4" color="primary" sx={{ mt: 1, mb: 1 }}>
                  ${parseFloat(plan.price).toFixed(2)}
                  <Typography component="span" variant="subtitle1" color="textSecondary">
                    /{getBillingPeriodLabel(billingCycle)}
                  </Typography>
                </Typography>
                
                {/* Show yearly savings */}
                {billingCycle === 'yearly' && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      icon={<Sparkles size={16} />}
                      label={`Save $${plan.yearlySavings}/year`}
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <List dense>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle size={16} color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ mt: 2 }}>
                  {plan.id === currentPlan.id && billingCycle === subscriptionStatus.billingCycle ? (
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      color="primary"
                      disabled
                    >
                      Current Plan
                    </Button>
                  ) : (
                    <Button 
                      fullWidth 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        setSelectedPlan(plan.id);
                        setSelectedBillingCycle(billingCycle);
                        setChangePlanDialogOpen(true);
                      }}
                    >
                      {plan.id === currentPlan.id 
                        ? `Switch to ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Billing` 
                        : `Choose ${plan.name}`}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ py: 3, px: 1 }}>
      {/* Page Title */}
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
        Billing & Subscriptions
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your subscription, payment methods, and billing history.
      </Typography>
      
      {/* Alert Message */}
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          onClose={handleAlertClose}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}
      
      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Tabs */}
      <Paper elevation={0} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<Package />} 
            label="Subscription" 
            iconPosition="start" 
          />
          <Tab 
            icon={<FileText />} 
            label="Payment History" 
            iconPosition="start" 
          />
          <Tab 
            icon={<BarChart />} 
            label="Plans Comparison" 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>
      
      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && renderSubscriptionTab()}
        {activeTab === 1 && renderPaymentHistoryTab()}
        {activeTab === 2 && renderPlansTab()}
      </Box>
      
      {/* Add Payment Method Dialog */}
      <Dialog open={addCardDialogOpen} onClose={handleAddCardClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Payment Method</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <TextField
              label="Card Number"
              name="number"
              value={cardDetails.number}
              onChange={handleCardDetailsChange}
              fullWidth
              margin="normal"
              placeholder="1234 5678 9012 3456"
              inputProps={{ maxLength: 19 }}
            />
            <TextField
              label="Cardholder Name"
              name="name"
              value={cardDetails.name}
              onChange={handleCardDetailsChange}
              fullWidth
              margin="normal"
              placeholder="John Doe"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Expiry Date"
                  name="expiry"
                  value={cardDetails.expiry}
                  onChange={handleCardDetailsChange}
                  fullWidth
                  margin="normal"
                  placeholder="MM/YY"
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="CVC"
                  name="cvc"
                  value={cardDetails.cvc}
                  onChange={handleCardDetailsChange}
                  fullWidth
                  margin="normal"
                  placeholder="123"
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Shield size={16} />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Your payment information is encrypted and secure.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddCardClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddCard}
            disabled={
              !cardDetails.number || 
              !cardDetails.name || 
              !cardDetails.expiry || 
              !cardDetails.cvc ||
              loading
            }
          >
            {loading ? <CircularProgress size={24} /> : 'Add Payment Method'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCancelSubscriptionClose} maxWidth="sm">
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <AlertCircle color="#f44336" size={48} style={{ marginBottom: 16 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Are you sure you want to cancel your subscription?
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your subscription will remain active until the end of the current billing period on {formatDate(subscriptionStatus.currentPeriodEnd)}.
            </Typography>
            <Typography variant="body1">
              You will lose access to:
            </Typography>
            <List dense>
              {currentPlan.features?.map((feature, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <XCircle size={16} color="#f44336" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubscriptionClose}>Keep Subscription</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleCancelSubscription}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialogOpen} onClose={handleChangePlanClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {trialInfo.isTrial ? 'Upgrade from Trial' : 'Change Subscription Plan'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            {trialInfo.isTrial && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {trialInfo.isExpired 
                  ? 'Your trial has expired. Upgrading will give you immediate access to all features.' 
                  : 'Upgrading from your trial will give you full access to all features.'}
              </Alert>
            )}
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              Select the plan you want to {trialInfo.isTrial ? 'subscribe to' : 'switch to'}:
            </Typography>
            
            {/* Plan Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="plan-select-label">Select Plan</InputLabel>
              <Select
                labelId="plan-select-label"
                value={selectedPlan || ''}
                onChange={(e) => setSelectedPlan(e.target.value)}
                label="Select Plan"
              >
                {subscriptionPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name} - ${parseFloat(plan.price).toFixed(2)}/{getBillingPeriodLabel(selectedBillingCycle)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Billing Cycle Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Billing Cycle:
              </Typography>
              <ToggleButtonGroup
                value={selectedBillingCycle}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setSelectedBillingCycle(newValue);
                }}
                aria-label="billing cycle"
                size="small"
                sx={{ mb: 1 }}
              >
                <ToggleButton value="monthly" aria-label="monthly billing">
                  Monthly
                </ToggleButton>
                <ToggleButton value="yearly" aria-label="yearly billing">
                  Yearly <Chip label="20% off" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                </ToggleButton>
              </ToggleButtonGroup>
              
              {selectedBillingCycle === 'yearly' && (
                <Typography variant="body2" color="success.main">
                  You'll save 20% with annual billing!
                </Typography>
              )}
            </FormControl>
            
            {/* Payment Method Selection */}
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Payment Method:
              </Typography>
              <ToggleButtonGroup
                value={selectedPaymentMethod}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue) setSelectedPaymentMethod(newValue);
                }}
                aria-label="payment method"
                size="small"
              >
                <ToggleButton value="card" aria-label="credit card payment">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCard size={16} />
                    Credit Card
                  </Box>
                </ToggleButton>
                <ToggleButton value="invoice" aria-label="bank transfer">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileText size={16} />
                    Bank Transfer
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
              
              {selectedPaymentMethod === 'invoice' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Bank Transfer Information
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Please use the following details to complete your payment:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                    <li>
                      <Typography variant="body2">Bank: <strong>Example Bank Ltd</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Account Name: <strong>Your Company Name</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Account Number: <strong>1234567890</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">Sort Code / Routing: <strong>01-02-03</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2">IBAN: <strong>GB29NWBK60161331926819</strong></Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ overflowWrap: 'break-word' }}>
                        Reference: <strong>{bankReferenceNumber}</strong>
                      </Typography>
                    </li>
                  </Box>
                  <Alert severity="info" sx={{ mt: 1 }} icon={<Clock size={16} />}>
                    Please allow 1-3 business days for your payment to be processed.
                  </Alert>
                </Box>
              )}
            </FormControl>
            
            {selectedPlan && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Plan Details
                </Typography>
                
                {!trialInfo.isTrial && currentPlan.id !== 'trial' && selectedPlan !== currentPlan.id && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    You are changing from {currentPlan.name} (${parseFloat(currentPlan.price).toFixed(2)}/{getBillingPeriodLabel(subscriptionStatus.billingCycle)}) 
                    to {subscriptionPlans.find(p => p.id === selectedPlan)?.name} (${parseFloat(subscriptionPlans.find(p => p.id === selectedPlan)?.price).toFixed(2)}/{getBillingPeriodLabel(selectedBillingCycle)}).
                  </Typography>
                )}
                
                {trialInfo.isTrial && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    You are upgrading from your Trial to the {subscriptionPlans.find(p => p.id === selectedPlan)?.name} plan (${parseFloat(subscriptionPlans.find(p => p.id === selectedPlan)?.price).toFixed(2)}/{getBillingPeriodLabel(selectedBillingCycle)}).
                  </Typography>
                )}
                
                <Box sx={{ mt: 2, mb: 2 }}>
                  {!trialInfo.isTrial && currentPlan.id !== 'trial' && (
                    <Typography variant="body2">
                      {parseFloat(subscriptionPlans.find(p => p.id === selectedPlan)?.price) > parseFloat(currentPlan.price) 
                        ? 'You will be charged the prorated difference immediately.' 
                        : 'Your account will be credited the prorated difference.'}
                    </Typography>
                  )}
                  
                  {trialInfo.isTrial && (
                    <Typography variant="body2">
                      {selectedPaymentMethod === 'card' 
                        ? `Your card will be charged $${parseFloat(subscriptionPlans.find(p => p.id === selectedPlan)?.price).toFixed(2)} when you confirm.`
                        : `Please complete your bank transfer of $${parseFloat(subscriptionPlans.find(p => p.id === selectedPlan)?.price).toFixed(2)} using the reference ${bankReferenceNumber}.`}
                    </Typography>
                  )}
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  New Plan Features
                </Typography>
                <List dense>
                  {subscriptionPlans.find(p => p.id === selectedPlan)?.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle size={16} color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleChangePlanClose}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleChangePlan}
            disabled={!selectedPlan || (selectedPlan === currentPlan.id && selectedBillingCycle === subscriptionStatus.billingCycle && !trialInfo.isTrial) || loading}
          >
            {loading ? <CircularProgress size={24} /> : trialInfo.isTrial ? 'Upgrade Now' : 'Change Plan'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Invoice Generation Dialog */}
      <Dialog 
        open={invoiceDialogOpen} 
        onClose={handleCloseInvoiceDialog} 
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            maxHeight: '90vh',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Invoice</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              startIcon={<Printer />} 
              variant="outlined" 
              size="small"
              onClick={handlePrintInvoice}
            >
              Print/Save PDF
            </Button>
            <IconButton size="small" onClick={handleCloseInvoiceDialog}>
              <XCircle size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {generatedInvoice && (
            <Box ref={invoiceRef} sx={{ bgcolor: 'white', p: 2, border: '1px solid #eee', borderRadius: 1 }}>
              {/* Invoice Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {generatedInvoice.companyDetails.name}
                  </Typography>
                  <Typography variant="body2">{generatedInvoice.companyDetails.address}</Typography>
                  <Typography variant="body2">{generatedInvoice.companyDetails.city}</Typography>
                  <Typography variant="body2">Phone: {generatedInvoice.companyDetails.phone}</Typography>
                  <Typography variant="body2">Email: {generatedInvoice.companyDetails.email}</Typography>
                  <Typography variant="body2">Web: {generatedInvoice.companyDetails.website}</Typography>
                </Box>
                <Box textAlign="right">
                  <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                    INVOICE
                  </Typography>
                  <Typography variant="body2"><strong>Invoice #:</strong> {generatedInvoice.id}</Typography>
                  <Typography variant="body2"><strong>Date:</strong> {formatDate(generatedInvoice.date)}</Typography>
                  <Typography variant="body2"><strong>Due Date:</strong> {formatDate(generatedInvoice.dueDate)}</Typography>
                  <Chip 
                    label={generatedInvoice.status} 
                    color={generatedInvoice.status === 'Paid' ? 'success' : 'warning'} 
                    size="small" 
                    sx={{ mt: 1 }} 
                  />
                </Box>
              </Box>
              
              {/* Bill To */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Bill To:</Typography>
                <Typography variant="body1">{generatedInvoice.customerDetails.name}</Typography>
                <Typography variant="body2">{generatedInvoice.customerDetails.address}</Typography>
                <Typography variant="body2">{generatedInvoice.customerDetails.city}</Typography>
                <Typography variant="body2">Email: {generatedInvoice.customerDetails.email}</Typography>
              </Box>
              
              {/* Service Period */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="body2">
                  <strong>Service Period: </strong> 
                  {formatDate(generatedInvoice.periodStart)} to {formatDate(generatedInvoice.periodEnd)}
                </Typography>
              </Box>
              
              {/* Items Table */}
              <TableContainer component={Paper} elevation={0} sx={{ mb: 4, border: '1px solid #eee' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="center"><strong>Quantity</strong></TableCell>
                      <TableCell align="right"><strong>Unit Price</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generatedInvoice.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${parseFloat(item.unitPrice).toFixed(2)}</TableCell>
                        <TableCell align="right">${parseFloat(item.amount).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Additional Notes Row */}
                    {generatedInvoice.additionalNotes && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ fontStyle: 'italic', color: 'success.main' }}>
                          Note: {generatedInvoice.additionalNotes}
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* Subtotal */}
                    <TableRow>
                      <TableCell rowSpan={3} colSpan={2} />
                      <TableCell align="right"><strong>Subtotal</strong></TableCell>
                      <TableCell align="right">${parseFloat(generatedInvoice.subtotal).toFixed(2)}</TableCell>
                    </TableRow>
                    
                    {/* Tax */}
                    <TableRow>
                      <TableCell align="right"><strong>Tax ({generatedInvoice.taxRate}%)</strong></TableCell>
                      <TableCell align="right">${parseFloat(generatedInvoice.taxAmount).toFixed(2)}</TableCell>
                    </TableRow>
                    
                    {/* Total */}
                    <TableRow>
                      <TableCell align="right" sx={{ borderBottom: '2px solid #000' }}><strong>Total</strong></TableCell>
                      <TableCell align="right" sx={{ borderBottom: '2px solid #000' }}><strong>${parseFloat(generatedInvoice.total).toFixed(2)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Payment Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Payment Information:</Typography>
                
                {generatedInvoice.paymentMethod === 'card' ? (
                  <Box>
                    <Typography variant="body2"><strong>Payment Method:</strong> Credit Card</Typography>
                    <Typography variant="body2">Your card will be automatically charged on the due date.</Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2"><strong>Payment Method:</strong> Bank Transfer</Typography>
                    <Typography variant="body2" gutterBottom><strong>Bank:</strong> {generatedInvoice.bankDetails.name}</Typography>
                    <Typography variant="body2"><strong>Account Number:</strong> {generatedInvoice.bankDetails.accountNumber}</Typography>
                    <Typography variant="body2"><strong>Routing Number:</strong> {generatedInvoice.bankDetails.routingNumber}</Typography>
                    <Typography variant="body2" gutterBottom><strong>IBAN:</strong> {generatedInvoice.bankDetails.iban}</Typography>
                    <Typography variant="body2" sx={{ mt: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                      <strong>Reference:</strong> {generatedInvoice.reference}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Please include this reference number with your payment.
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {/* Terms */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Terms & Conditions:</Typography>
                <Typography variant="body2" gutterBottom>
                  1. Payment is due within 14 days of invoice date.
                </Typography>
                <Typography variant="body2" gutterBottom>
                  2. This subscription will automatically renew on {formatDate(generatedInvoice.periodEnd)} unless canceled.
                </Typography>
                <Typography variant="body2">
                  3. For questions regarding this invoice, please contact {generatedInvoice.companyDetails.email}.
                </Typography>
              </Box>
              
              {/* Thank You Note */}
              <Box sx={{ textAlign: 'center', mt: 4, borderTop: '1px solid #eee', pt: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  Thank you for your business!
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BillingDashboard;